import * as destinationPackageDeliveryService from "../../../../services/destinationPackageDelivery";
import * as settings from "../../../../services/settings";

import { Button, Col, Form, Input, Row, Select, Spin, Typography, notification } from "antd";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import BtnHistory from "../../../../Components/model/button/history";
import ContentType from "../../../../../configs/contentType";
import { formatDeliveryStatusLabel } from "../../../../utils/deliveryStatusLabel";
import permissions from "../../../../../configs/permission";
import { useAccessToken } from "../../../../contexts/AccessTokenContext";
import { useMemo } from "react";
import { usePermission } from "../../../../hooks/usePermission";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const { TextArea } = Input;

export const DestinationPackageDeliveryCreatePage = () => {
  const { accessToken } = useAccessToken();
  const { hasPermission, isSuperuser } = usePermission();
  const [formDestinationPackageDelivery] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation("destinationpackagedelivery", "buttons", "common");
  const { t: tOps } = useTranslation("options", { keyPrefix: "Package" });

  const location = useLocation();
  const data = location.state;
  const navigation = useNavigate();

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };

  const setDetailData = (data) => {
    formDestinationPackageDelivery.setFieldValue("delivery_status", data.delivery_status);
    formDestinationPackageDelivery.setFieldValue("note", data.note);
  };

  const onSubmit = async () => {
    const values = await formDestinationPackageDelivery.getFieldsValue();
    const { note, delivery_status } = values || {};

    const prepareDestinationPackageDelivery = {
      note: note || "",
      delivery_status: delivery_status.toLowerCase() || "",
    };
    const updateID = data.destinationPackageDeliveryData.id;
    setLoading(true);
    try {
      const response = await destinationPackageDeliveryService.changeDestinationDeliveryPackage(
        accessToken,
        updateID,
        prepareDestinationPackageDelivery
      );

      if (response !== null || response != undefined) {
        setLoading(false);
        openNotificationWithIcon(
          settings.NotificationType.SUCCESS,
          <>{"Success."}</>,
          <>{"Update origin package success"}</>
        );
      }
    } catch (err) {
      console.error("[DestinationPackageDeliveryCreatePage] onSubmit: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    if (!accessToken) return;
    setLoading(false);
    if (data?.type === "EDIT") {
      setDetailData(data.destinationPackageDeliveryData);
    }
  }, [accessToken]);

  const handleListDeliveryType = (value) => {
    let parseValue = value.delivery_type.toUpperCase();
    let destination_description;
    if (parseValue === "Normal".toUpperCase()) {
      destination_description = tOps("Delivery_Type.Normal");
    } else if (parseValue === "SELF".toUpperCase()) {
      destination_description = tOps("Delivery_Type.Self");
    } else if (parseValue === "Private".toUpperCase()) {
      destination_description = tOps("Delivery_Type.Private");
    } else {
      destination_description = tOps("Delivery_Type.Express");
    }
    return <p>{destination_description}</p>;
  };

  const selectDestinationStatus = [
    { value: "open", label: formatDeliveryStatusLabel("open", i18n.language, tOps) },
    { value: "close", label: formatDeliveryStatusLabel("close", i18n.language, tOps) },
    { value: "deliver", label: formatDeliveryStatusLabel("deliver", i18n.language, tOps) },
    { value: "return", label: formatDeliveryStatusLabel("return", i18n.language, tOps) },
  ];

  const textCustomer = useMemo(() => {
    if (data) {
      const { app_company, app_sale, app_customer } = data.destinationPackageDeliveryData ?? {};
      return `${app_company.company_code}-${app_sale.sale_code}${app_customer.customer_code}`;
    }
    return "";
  }, [data]);

  if (!data) {
    return <Navigate to="/package/destinationpackagedelivery" replace />;
  }

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row style={{ overflowX: "auto" }}>
          <Col span={24}>
            <div className="flex justify-between mb-4">
              <Title level={2}>{t("Destination_Package_Delivery_Change.DPD_Change_Title")}</Title>
              {data?.type === "EDIT" && (
                <BtnHistory
                  titleRef={textCustomer}
                  object_id={data?.destinationPackageDeliveryData?.id}
                  content_type_id={[ContentType.DestinationPackageDelivery]}
                />
              )}
            </div>
          </Col>
          <Col span={22} style={{ minWidth: 640 }}>
            <Form form={formDestinationPackageDelivery} labelAlign="left" labelCol={{ flex: "140px" }}>
              <Row gutter={12}>
                <Col span={8} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Destination_Package_Delivery_Change.DPD_Company") + ":"}
                    name="company"
                    style={{ marginBottom: "1rem" }}
                    hasFeedback
                  >
                    <Text>{`${data.destinationPackageDeliveryData.app_company.company_code} (${data.destinationPackageDeliveryData.app_company.company_name})`}</Text>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Destination_Package_Delivery_Change.DPD_Sales") + ":"}
                    name="sales"
                    style={{ marginBottom: "1rem" }}
                    hasFeedback
                  >
                    <Text>{`${data.destinationPackageDeliveryData.app_sale.sale_code} (${data.destinationPackageDeliveryData.app_company.company_code}-${data.destinationPackageDeliveryData.app_sale.sale_name})`}</Text>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Destination_Package_Delivery_Change.DPD_Customer") + ":"}
                    name="customer"
                    style={{ marginBottom: "1rem" }}
                  >
                    <Text>{textCustomer}</Text>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Destination_Package_Delivery_Change.DPD_Destination_Package") + ":"}
                    name="destination_package"
                    style={{ marginBottom: "1rem" }}
                    hasFeedback
                  >
                    <Text>{data.destinationPackageDeliveryData.app_destinationpackage.reference}</Text>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Destination_Package_Delivery_Change.DPD_Destination_Delivery") + ":"}
                    name="delivery_type"
                    style={{ marginBottom: "1rem" }}
                    hasFeedback
                  >
                    <Text>{handleListDeliveryType(data.destinationPackageDeliveryData.app_destinationdelivery)}</Text>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Destination_Package_Delivery_Change.DPD_Delivery_Status") + ":"}
                    name="delivery_status"
                    style={{ marginBottom: "1rem" }}
                    hasFeedback
                  >
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      options={selectDestinationStatus}
                      defaultValue={"open"}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={8} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Destination_Package_Delivery_Change.DPD_Total") + ":"}
                    name="total_received"
                    style={{ marginBottom: "1rem" }}
                    hasFeedback
                  >
                    <Text>{data.destinationPackageDeliveryData.total_received}</Text>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Destination_Package_Delivery_Change.DPD_Note") + ":"}
                    name="note"
                    style={{ marginBottom: "1rem" }}
                  >
                    <TextArea
                      allowClear={true}
                      rows={4}
                      value={formDestinationPackageDelivery.getFieldValue().header}
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row align="middle" justify="end" style={{ marginTop: "20px" }}>
          {hasPermission(permissions.DestinationPackageDelivery.CHANGE) && (
            <Button type="primary" onClick={onSubmit} style={{ marginRight: "10px" }}>
              {t("BTN_Save", { ns: "buttons" })}
            </Button>
          )}
          <Button
            type="default"
            onClick={() => {
              navigation("/package/destinationpackagedelivery");
            }}
          >
            {t("BTN_Cancel", { ns: "buttons" })}
          </Button>
        </Row>
      </Spin>
    </>
  );
};
